import { Icon } from '@mui/material';
import { QRCode } from 'antd';
import { FunctionComponent } from 'react';
import { Link } from 'react-router-dom';

import { UnlockSerieResponse } from '../../../shared/api/request';
import { Category } from '../../../shared/types/Category';
import { Collection as CollectionType } from '../../../shared/types/Collection';
import { Serie } from '../../../shared/types/Serie';

import HorizontalList from '../../../shared/components/HorizontalList';
import { useAuth } from '../../../shared/context/TokenContext';
import Collection from '../../../shared/cover/Collection';
import DefaultCoverV2 from '../../../shared/cover/DefaultCoverV2';
import '../../../shared/css/fontStyle.css';
import '../../../shared/css/ui.css';
import '../../css/HomeV2.css';
import AppPromoCard from './AppPromoCard';
import StudentAds from './StudentAds';
import UnlockSerieCard from './UnlockSerieCard';

const APP_LINK = 'https://nabook.onelink.me/cyS5/r9swckn1';
const EMAIL_INSTALL_LINK =
  'https://5f6b0c5f.sibforms.com/serve/MUIEAOPhDiIQfqR6hciMd7COdRm4KLN-lrmaheNr7gOGFYSEniGMJeYXOdjVLq2l7YJKT04Tor2o2niXW6hRclpavG1stMvbtm4Gcm17NoFGRPZTn2i65JJ6jryefn6TPBd46ObBdMFC4dFM-utsoELiVbyVVTboNf1v9Ds-6YuRcddr3PlcecAn8L9CbivBgaASHKvWWaq2jdng';

// Collection categories hold collections (uid + imgCollection), not series
export const isCollectionCategory = (category: Category) => category.typeContent === 'collection';

interface HomeVisualProps {
  assignations: Serie[];
  cats: Category[];
  isLoading: boolean;
  errorLoading: string | null;
  playlist: Serie[] | undefined;
  highlightedSerieIds: number[];
  onClickSerie: (serie: Serie) => void;
  onRetry: () => void;
  onUnlockSerie: (code: string) => Promise<UnlockSerieResponse>;
}

const HomeSkeleton = () => (
  <div className="nbk-home" aria-busy="true" aria-label="Chargement des séries">
    {[0, 1, 2].map(row => (
      <div key={row} className="nbk-skeleton-row">
        <div className="nbk-skeleton nbk-skeleton--title" />
        <div className="nbk-skeleton-row__track">
          {[0, 1, 2, 3, 4, 5, 6, 7].map(cover => (
            <div key={cover} className="nbk-skeleton nbk-skeleton--cover" />
          ))}
        </div>
      </div>
    ))}
  </div>
);

const HomeVisual: FunctionComponent<HomeVisualProps> = (props: HomeVisualProps) => {
  const { isEdifice, isTeacher } = useAuth();

  const {
    assignations,
    cats,
    errorLoading,
    highlightedSerieIds,
    isLoading,
    playlist,
    onClickSerie,
    onRetry,
    onUnlockSerie,
  } = props;

  if (isLoading) return <HomeSkeleton />;

  if (errorLoading) {
    return (
      <div className="nbk-home nbk-home--centered">
        <section className="nbk-card nbk-state" role="alert">
          <div className="nbk-card__icon nbk-card__icon--muted" aria-hidden="true">
            <Icon>cloud_off</Icon>
          </div>
          <h2 className="nbk-card__title">Impossible de charger les séries</h2>
          <p className="nbk-card__text">
            Vérifiez votre connexion internet puis réessayez. Vous pouvez aussi télécharger l'application mobile pour accéder à
            toutes les séries.
          </p>
          <button type="button" className="nbk-btn nbk-btn--primary" onClick={onRetry}>
            <Icon aria-hidden="true">refresh</Icon>
            Réessayer
          </button>
        </section>
      </div>
    );
  }

  if (cats.length === 0) {
    return (
      <div className="nbk-home nbk-home--centered">
        <section className="nbk-card nbk-state">
          <h2 className="nbk-card__title">Lisez Nabook sur votre téléphone</h2>
          <p className="nbk-card__text">Scannez ce QR code avec votre mobile pour télécharger l'application.</p>
          <a className="nbk-state__qr" href={APP_LINK} target="_blank" rel="noreferrer" aria-label="Télécharger l'application Nabook">
            <QRCode size={180} value={APP_LINK} bgColor="white" bordered={false} />
          </a>
          <p className="nbk-state__separator">ou</p>
          <a className="nbk-btn nbk-btn--ghost" href={EMAIL_INSTALL_LINK} target="_blank" rel="noreferrer">
            <Icon aria-hidden="true">mail_outline</Icon>
            Recevoir le lien d'installation par email
          </a>
        </section>
      </div>
    );
  }

  const isEdificeUser = !!isEdifice && isEdifice();

  const renderSeries = (series: Serie[]) =>
    series.map(s => (
      <DefaultCoverV2
        key={s.uid}
        serie={s}
        onClickCover={() => onClickSerie(s)}
        badge={highlightedSerieIds.includes(s.id) ? 'Débloquée' : undefined}
      />
    ));

  return (
    <div className="nbk-home">
      {!isEdificeUser ? (
        <div className="nbk-hero">
          <UnlockSerieCard onUnlockSerie={onUnlockSerie} />
          <AppPromoCard />
        </div>
      ) : null}

      {playlist && playlist.length > 0 ? (
        <HorizontalList title="Pile à lire" subtitle="Reprenez vos lectures en cours">
          {renderSeries(playlist)}
        </HorizontalList>
      ) : null}

      {assignations && assignations.length > 0 ? (
        <HorizontalList title="Devoir de lecture">{renderSeries(assignations)}</HorizontalList>
      ) : null}

      {cats.map((c, index) =>
        isCollectionCategory(c) ? (
          <HorizontalList
            key={c.uid || index}
            title={c.name}
            subtitle={c.subtitle}
            action={
              <Link to="/collection" className="nbk-row__link">
                Tout voir
                <Icon aria-hidden="true">chevron_right</Icon>
              </Link>
            }>
            {(c.content as unknown as CollectionType[]).map(coll => (
              <Collection key={coll.uid} collection={coll} />
            ))}
          </HorizontalList>
        ) : (
          <HorizontalList key={c.uid || index} title={c.name} subtitle={c.subtitle}>
            {renderSeries(c.content)}
          </HorizontalList>
        ),
      )}

      {isEdificeUser && isTeacher && !isTeacher() ? <StudentAds /> : null}
      {/* {isEdifice && isTeacher && isEdifice() && isTeacher() ? <TeacherSurvey /> : null} */}
    </div>
  );
};

export default HomeVisual;
